import type {
    HandoffObservation,
    HandoffSignal,
} from "./handoff-observation.contract";
import { attemptEvidence } from "./handoff-evidence";
import type IHandoffRuntime from "./handoff-runtime.interface";

const DEADLINE_MS = 3_000;

type TerminalSignal = Exclude<
    HandoffSignal,
    { evidence: "navigation_requested" }
>;
type CancelReason = Extract<
    HandoffObservation,
    { evidence: "observation_cancelled" }
>["cancel_reason"];

export function createHandoffObserver({
    runtime,
    attemptId,
    capture,
    fallback,
}: {
    runtime: IHandoffRuntime;
    attemptId: string;
    capture: (observation: HandoffObservation) => void;
    fallback: (handedOff: boolean) => void;
}) {
    let startedAt = runtime.now();
    let sessionId: string | undefined;
    let state: "idle" | "observing" | "finished" = "idle";
    let navigationRecorded = false;
    let unsubscribePagehide = () => {};
    let unsubscribeVisibility = () => {};
    let cancelDeadline = () => {};

    const emit = (signal: HandoffSignal) => {
        capture({
            ...attemptEvidence(attemptId, "one_tap_v2"),
            ...(sessionId
                ? { session_state: "resolved", contact_session_id: sessionId }
                : { session_state: "unresolved" }),
            detector_version: "one_tap_v2",
            deadline_ms: DEADLINE_MS,
            elapsed_ms: Math.max(0, runtime.now() - startedAt),
            ...signal,
        });
    };

    const finish = (signal: TerminalSignal) => {
        if (state === "finished") return;
        state = "finished";
        unsubscribePagehide();
        unsubscribeVisibility();
        cancelDeadline();
        emit(signal);
        if (signal.evidence === "visible_at_deadline") fallback(true);
        if (
            signal.evidence === "navigation_not_requested" ||
            signal.evidence === "navigation_callback_threw"
        )
            fallback(false);
    };

    return {
        run(
            resolvedSessionId: string,
            navigate: (navigationRequested: () => void) => boolean,
        ) {
            if (state !== "idle") return;
            state = "observing";
            sessionId = resolvedSessionId;
            startedAt = runtime.now();
            unsubscribePagehide = runtime.onPagehide(() =>
                finish({ evidence: "pagehide_observed" }),
            );
            unsubscribeVisibility = runtime.onVisibilityChange(() => {
                if (runtime.visibility() === "hidden")
                    finish({ evidence: "visibility_hidden_observed" });
            });
            cancelDeadline = runtime.after(DEADLINE_MS, () =>
                finish({
                    evidence:
                        runtime.visibility() === "visible"
                            ? "visible_at_deadline"
                            : "visibility_hidden_observed",
                }),
            );
            try {
                const navigates = navigate(() => {
                    if (state !== "observing" || navigationRecorded) return;
                    navigationRecorded = true;
                    emit({ evidence: "navigation_requested" });
                });
                if (!navigates)
                    finish({ evidence: "navigation_not_requested" });
            } catch {
                finish({ evidence: "navigation_callback_threw" });
            }
        },
        cancel(reason: CancelReason) {
            finish({
                evidence: "observation_cancelled",
                cancel_reason: reason,
            });
        },
    };
}
